import { Announcement, getWidgetScalePosition } from './Announcement';
import { AnnouncementGroup } from './AnnouncementGroup';
import { Scheduler, SchedulerItem } from '../../Utility/Scheduler';
import { AnnouncementWidget } from './AnnouncementWidget';
import { ZoneInfo } from '../../Zone';
import { AnnouncementTimelineSlot, AnnouncementWidgetTimelineSlot } from './types';
import { isNullish } from '../../Utility/Nullish';

/**
 * Announcement Controller
 */
class AnnouncementController {
    public constructor(widget: AnnouncementWidget, zoneInfo: ZoneInfo) {
        this.#widget = widget;
        this.zoneInfo = zoneInfo;
    }

    /**
     * Widget Object
     */
    readonly #widget: AnnouncementWidget;

    /**
     * Zone Info Object
     */
    public zoneInfo: ZoneInfo;

    /**
     * Announcements Array
     */
    public announcementGroup?: AnnouncementGroup;

    /**
     * Last Announcement to Play
     *
     * taken from original announcement widget
     */
    #lastAnnouncement?: Announcement;

    /**
     * Last Lineup
     *
     * taken from original announcement widget
     */
    #lastLineup?: AnnouncementWidgetTimelineSlot[];

    /**
     * Last Dimensions Object
     *
     * taken from original announcement widget
     */
    #lastDimensions: { width?: number; height?: number } = {
        width: undefined,
        height: undefined,
    };

    /**
     * CSS Object used to help with scaling
     *
     * taken from original announcement widget
     * @see {@link http://api.jquery.com/css/#css-properties}
     */
    #initCssObject?: Record<string, string>;

    /**
     * Scheduler to handle update check for every 5 minutes
     */
    #updateActionsScheduler?: SchedulerItem;

    /**
     * function to return announcement group's announcements
     */
    public announcements(): AnnouncementGroup | undefined {
        return this.announcementGroup;
    }

    /**
     * purges lineup when loop is aborted
     */
    public purgeLineup(): void {
        this.#lastLineup = undefined;
    }

    /**
     * creates AnnouncementGroup instance and checks for updates every 10 seconds
     */
    async initialize(): Promise<void> {
        this.announcementGroup = new AnnouncementGroup(this, this.#widget);

        // select a random number of seconds from 1-3600 for only the first pull.
        // set randomly to stagger subsequent api calls which will prevent API
        // overload.
        setTimeout(() => {
            this.#updateActionsScheduler = Scheduler.New()
                .Called(`update_announcement_${this.#widget.id}`)
                .HasAction(() => {
                    this.announcementGroup?.processUpdateActions();
                })
                .RunEvery(4, 'hours')
                .Starting('1/1/2000');
        }, (Math.floor(Math.random() * 3600) + 1) * 1000);

        await this.announcementGroup.processUpdateActions();
    }

    /**
     * sets the widget scale factor. If no parameters are passed, it uses the last
     * known dimensions used.
     * @param {number} [width] - Stage Width
     * @param {number} [height] - Stage Height
     */
    setWidgetScale(width?: number, height?: number): void {
        if (this.#lastDimensions?.width === width && this.#lastDimensions?.height === height) {
            //dimensions have not changed since last query of the server, skip
            //re-sizing
            return;
        }

        if (typeof width === 'number' && width >= 0 && typeof height === 'number' && height >= 0) {
            this.#lastDimensions = {
                width: width,
                height: height,
            };
        }

        this.#initCssObject = getWidgetScalePosition(
            this.zoneInfo.w,
            this.zoneInfo.h,
            this.#lastDimensions.width || NaN,
            this.#lastDimensions.height || NaN
        );

        const maybeContainerCanvasElement = this.#widget.wrapperElement.querySelector<HTMLElement>(
            '.announcement-container-canvas-class'
        );
        if (maybeContainerCanvasElement) {
            Object.assign(maybeContainerCanvasElement.style, this.#initCssObject);
        }
    }

    /**
     * return slots to use for Timeline
     */
    getSlots(numberOfSlots: number, timeRemaining: number): AnnouncementWidgetTimelineSlot[] {
        if (!this.announcementGroup) {
            //should not be possible
            throw new Error(`announcementGroup is undefined`);
        }

        this.announcementGroup.liftProvisionLocks();

        //ok, we need to "move next" until numslots and time_remaining is done
        const origLastOrderId = this.announcementGroup.announcementsLastOrderId;
        const slots = [];

        if (this.#lastLineup) {
            const lastItem = this.#lastLineup[this.#lastLineup.length - 1];
            if (lastItem && lastItem.params) {
                this.announcementGroup.announcementsLastOrderId = lastItem.params.order();
            }
        }

        //A boolean describing if the next loop's slot will be part of a group
        let continueGrouping = false;
        while (continueGrouping || (numberOfSlots > 0 && timeRemaining > 0)) {
            const next = this.announcementGroup.moveNext();

            if (isNullish(next)) {
                break;
            }

            if (next.canPlay()) {
                const timing = next.duration;

                if (continueGrouping || timing <= timeRemaining) {
                    numberOfSlots--;
                    timeRemaining -= timing;
                    next.provisionIsLocked = true;

                    const nextGroupId = !isNullish(next.configuration.groupId)
                        ? parseInt(next.configuration.groupId)
                        : undefined;

                    const slot = this.#widget.generateSlot(timing, nextGroupId, next);
                    slots.push(slot);

                    const peekedItem = this.announcementGroup.peekNext();
                    const peekedGroupId =
                        !isNullish(peekedItem) && !isNullish(peekedItem.configuration.groupId)
                            ? parseInt(peekedItem.configuration.groupId)
                            : undefined;

                    //Determine if we should continue forcing the loop to continue
                    // due to groups. The ordering check is to ensure that we have
                    // not looped around and are not grouping duplicate items
                    // example: All content on a playlist are in a single group
                    // NOTE: the current logic can result in extending the loop
                    // of content beyond the loop's actual allowed maximum time.
                    continueGrouping = Boolean(
                        !isNullish(peekedItem) &&
                            !isNullish(peekedGroupId) &&
                            peekedGroupId === nextGroupId &&
                            peekedItem.order() > next.order()
                    );
                } else {
                    //next eligible announcement exceeds slot
                    break;
                }
            }
        }

        this.announcementGroup.announcementsLastOrderId = origLastOrderId;

        this.#lastLineup = slots;

        return slots;
    }

    /**
     * @todo: Leave comments to clarify which Timeline type this is for
     * return slots to use for Timeline
     */
    getSlotsLinear(timeBased: boolean, minimumLoopTime: number): AnnouncementWidgetTimelineSlot[] {
        if (!this.announcementGroup) {
            //should not be possible
            throw new Error('impossible state: announcementGroup is undefined');
        }

        this.announcementGroup.liftProvisionLocks();

        if (timeBased) {
            this.#lastLineup = this._queryContentSlotsTimeBased(minimumLoopTime);
        } else {
            this.#lastLineup = this._queryContentSlotsLinear();
        }

        return this.#lastLineup;
    }

    /**
     * Return slots to use with Time-Based Timeline
     * @param {number} minimumLoopTime - The minimum amount of time the total
     * duration of the slots should take up.
     */
    protected _queryContentSlotsTimeBased(minimumLoopTime: number): AnnouncementTimelineSlot[] {
        if (!this.announcementGroup) {
            //should not be possible
            throw new Error(`announcementGroup is undefined`);
        }

        const slots: AnnouncementTimelineSlot[] = [];
        let usedTime = 0;
        let continueGrouping = false;

        do {
            const currentAnnouncementItem = this.announcementGroup.moveNext();

            if (isNullish(currentAnnouncementItem)) {
                break;
            }

            usedTime += currentAnnouncementItem.duration;
            currentAnnouncementItem.provisionIsLocked = true;

            const slot = this.#widget.generateSlot(
                currentAnnouncementItem.duration,
                undefined,
                currentAnnouncementItem
            );
            slots.push(slot);

            const nextAnnouncementItem = this.announcementGroup.peekNext();
            continueGrouping = Boolean(
                nextAnnouncementItem &&
                    !isNullish(nextAnnouncementItem.configuration.groupId) &&
                    nextAnnouncementItem.configuration.groupId ===
                        currentAnnouncementItem.configuration.groupId &&
                    nextAnnouncementItem.order() > currentAnnouncementItem.order()
            );
        } while (continueGrouping || usedTime < minimumLoopTime);

        return slots;
    }

    /**
     * Return slots to use with Linear Timeline
     */
    protected _queryContentSlotsLinear(): AnnouncementTimelineSlot[] {
        if (!this.announcementGroup) {
            //should not be possible
            throw new Error(`announcementGroup is undefined`);
        }

        const slots: AnnouncementTimelineSlot[] = [];

        this.announcementGroup.manifest
            .sort((announcementItemA, announcementItemB) => {
                return announcementItemA.order() - announcementItemB.order();
            })
            .forEach((announcementItem) => {
                if (announcementItem.canPlay()) {
                    announcementItem.provisionIsLocked = true;

                    const slot = this.#widget.generateSlot(
                        announcementItem.duration,
                        isNullish(announcementItem.configuration.groupId)
                            ? undefined
                            : parseInt(announcementItem.configuration.groupId),
                        announcementItem
                    );
                    slots.push(slot);
                }
            });

        return slots;
    }

    /**
     * retrieves last announcement played
     */
    lastOrderedAnnouncement(announcement?: Announcement): Announcement | undefined {
        if (!isNullish(announcement)) {
            this.#lastAnnouncement = announcement;

            if (this.announcementGroup) {
                this.announcementGroup.announcementsLastOrderId = announcement.order();
            }
        }

        return this.#lastAnnouncement;
    }

    /**
     * Stop Scheduler that updates widget content
     */
    stopScheduler(): void {
        if (this.#updateActionsScheduler) {
            Scheduler.Delete(this.#updateActionsScheduler.Name);
            this.#updateActionsScheduler = undefined;
        }
    }

    async deprovision(): Promise<void> {
        this.stopScheduler();
        await this.announcementGroup?.deprovision();
    }
}

export { AnnouncementController };
